import { z } from 'zod';
import { GeoSchema, I18nStringSchema, SlugSchema, TimestampSchema } from './primitives.js';
import { HoursSchema } from './branding.js';
import { RecentReviewSchema, RECENT_REVIEWS_LIMIT } from './review.js';

/**
 * Public denormalised directory row backing find.kilatcarwash.net
 * (feature: network-discovery). One row per opted-in outlet at
 * `outletDirectory/{outletId}`; rules allow public read, super-admin /
 * aggregation-job write. Composite index: (listedInFindDirectory, rating desc).
 */
export const OutletDirectorySchema = z.object({
  id: z.string().describe('Doc id — equals the source outletId'),
  tenantId: z.string().describe('Owning tenant; used by curation tooling, never shown publicly'),
  platformBrandId: SlugSchema.optional().describe(
    "Owning tenant's platform brand (ADR-0017), denormalised so a super-admin can LIST their brand's rows with where('platformBrandId','==',brandId) — Firestore evaluates a list query against the rule, so the rule must be able to decide from the row alone. Always written by buildDirectoryRow; optional on read only so rows predating the backfill (packages/config-seed/src/migrate-private-tenant-data.ts) still render on find",
  ),
  name: z
    .string()
    .min(1)
    .max(80)
    .describe('Outlet display name, denormalised from OutletProfile.name'),
  slug: SlugSchema.describe('Tenant slug for the deep link {slug}.app.kilatcarwash.net'),
  directorySlug: SlugSchema.describe(
    'Globally unique URL key for /o/{directorySlug}; aggregation job derives {tenantSlug}-{outletSlug} and de-dupes collisions',
  ),
  address: z.string().describe('Human-readable address shown on cards and the detail page'),
  geo: GeoSchema.describe('Outlet location for map pins and distance sorting'),
  primaryPhotoUrl: z
    .string()
    .url()
    .optional()
    .describe('Hero photo; UI falls back to a branded placeholder when absent'),
  photoUrls: z
    .array(z.string().url())
    .max(12)
    .default([])
    .describe('Gallery photos for the detail page; primaryPhotoUrl is the card thumbnail'),
  contact: z
    .object({
      phone: z.string().optional().describe('E.164 phone for the tap-to-call link'),
      whatsapp: z.string().optional().describe('Raw WhatsApp number; UI derives the wa.me link'),
    })
    .optional()
    .describe('Public contact channels, denormalised from the outlet profile'),
  servicesPreview: z
    .array(
      z.object({
        name: I18nStringSchema.describe('Service display name'),
        priceFromSen: z
          .number()
          .int()
          .nonnegative()
          .optional()
          .describe('Lowest price across vehicle types, in sen'),
      }),
    )
    .max(8)
    .default([])
    .describe('Teaser of services on the detail page; the full catalog lives in the tenant PWA'),
  rating: z
    .number()
    .min(0)
    .max(5)
    .default(0)
    .describe('Average stars aggregated from reviews; 0 = not yet rated'),
  ratingCount: z
    .number()
    .int()
    .nonnegative()
    .default(0)
    .describe('Number of reviews behind the average; 0 hides the stars in UI'),
  recentReviews: z
    .array(RecentReviewSchema)
    .max(RECENT_REVIEWS_LIMIT)
    .default([])
    .describe(
      'Latest non-hidden ANONYMISED reviews (first name only, no uid) for the detail page; owned by the reviews aggregation, preserved across directory re-projections',
    ),
  currentWaitMinutes: z
    .number()
    .int()
    .nonnegative()
    .nullable()
    .default(null)
    .describe(
      'Avg queue wait over the last hour, snapshotted by the aggregation job; null = unknown',
    ),
  hours: HoursSchema.describe(
    'Opening-hours snapshot feeding computeHoursStatus() for the open-now pill',
  ),
  listedInFindDirectory: z
    .boolean()
    .describe('Mirror of tenant branding opt-in; false rows are excluded from public queries'),
  featured: z
    .boolean()
    .default(false)
    .describe('Super-admin spotlight; boosts the row in the default sort'),
  verified: z
    .boolean()
    .default(false)
    .describe(
      "Super-admin-granted trust badge — renders a check on the find card + detail. Sourced from the owning tenant's recognition flags via buildDirectoryRow (growth §09); NON-money",
    ),
  founding: z
    .boolean()
    .default(false)
    .describe(
      "Super-admin-granted founding-operator status — renders a status pill on find. Sourced from the owning tenant's recognition flags via buildDirectoryRow (growth §09); NON-money",
    ),
  curatedHidden: z
    .boolean()
    .default(false)
    .describe(
      'Super-admin removal override. Survives directorySync re-projection (unlike a direct listedInFindDirectory write); while set, the projection forces listedInFindDirectory=false and the read rule keeps the row invisible to the public. The REASON lives in the private directoryCuration/{outletId} doc, never here',
    ),
  updatedAt: TimestampSchema.describe('Last aggregation write'),
});
export type OutletDirectoryRow = z.infer<typeof OutletDirectorySchema>;
